// A surface that understands gestures: wrap anything in it and give it onTap, onDoubleTap,
// onLongPress, onSwipe or onDrag. ListRow, SwipeRow, Pager and the sheets are built on it.
import { forwardRef } from "react";
import { View } from "tamagui";
import { useGestures, type GestureHandlers, type GestureOptions } from "./useGestures";

export interface GestureAreaProps
  extends
    Omit<React.ComponentProps<typeof View>, keyof GestureHandlers | "onPress">,
    GestureHandlers,
    GestureOptions {}

export const GestureArea = forwardRef<React.ComponentRef<typeof View>, GestureAreaProps>(
  function GestureArea(
    {
      onTap,
      onDoubleTap,
      onLongPress,
      onSwipe,
      onDrag,
      axis,
      longPressMs,
      doubleTapMs,
      swipeDistance,
      disabled,
      ...rest
    },
    ref,
  ) {
    const bindings = useGestures(
      { onTap, onDoubleTap, onLongPress, onSwipe, onDrag },
      {
        ...(axis === undefined ? {} : { axis }),
        ...(longPressMs === undefined ? {} : { longPressMs }),
        ...(doubleTapMs === undefined ? {} : { doubleTapMs }),
        ...(swipeDistance === undefined ? {} : { swipeDistance }),
        ...(disabled === undefined ? {} : { disabled }),
      },
    );
    const { style, ...handlers } = bindings;
    // The caller's own handlers run first, then the gesture layer's, so neither silences the other.
    const merged: Record<string, unknown> = { ...handlers };
    const own: Record<string, unknown> = rest;
    for (const key of Object.keys(handlers)) {
      const mine = own[key];
      const gesture = merged[key];
      if (typeof mine === "function" && typeof gesture === "function") {
        merged[key] = (event: unknown) => {
          mine(event);
          gesture(event);
        };
      } else if (typeof mine === "function") {
        merged[key] = mine;
      }
    }
    const callerStyle: object =
      typeof rest.style === "object" && rest.style !== null && !Array.isArray(rest.style)
        ? rest.style
        : {};
    // Tamagui types pointer events as React Native responder events; on the web they are DOM events.
    // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- the DOM and RN event types do not overlap
    const dom = merged as Partial<React.ComponentProps<typeof View>>;
    // nosemgrep: rules.typescript.react.best-practice.react-props-spreading -- props are typed as the component's own
    return <View ref={ref} {...rest} {...dom} style={{ ...callerStyle, ...style }} />;
  },
);
